// jobFields — the job form's rules, with no React in them.
//
// The classic app can create and edit a job; the rebuild shipped without that
// at all (docs/REBUILD_PARITY.md, "Jobs"). This module is the half of putting
// it back that can be tested without a browser: what the form starts from,
// what it sends, and which fields a given role is allowed to set.
//
// The contract is server/routes/jobs.js POST / and PUT /:id. Every field named
// there is named here, because a field the form does not send is a field the
// person cannot set:
//
//   title*        description     estimated_time   user_id      assignee_ids[]
//   client_id     property_id     status           job_type     recurrence
//   scheduled_date               scheduled_end_date
//   pay_amount    pay_type        status_note (PUT only)
//
// Vocabulary is duplicated from server/utils/scheduling.js deliberately: the
// server normalises anything it does not recognise to null, so a form offering
// a value the server drops would silently lose the person's choice. The test
// asserts the two lists agree.

export const JOB_STATUSES = ['todo', 'active', 'paused', 'completed', 'archived'];
export const JOB_TYPES = ['maintenance', 'project'];
// Order is the server's, shortest interval first, and jobFields.test.js
// asserts this array EQUALS sched.RECURRENCES — the "Repeats" dropdown in
// JobForm.js maps over it, so this line is also the order a person reads.
//
// There is deliberately no 'daily'. It was offered for a few hours on
// 2026-09-11 and withdrawn by the owner the same day: "When completing the
// job, the job is complete - Don't spawn another job." A repeat and leftover
// work are different things, and the second belongs to the end-of-day review
// — see server/utils/scheduling.js RECURRENCES for the distinction in full.
export const RECURRENCES = ['weekly', 'biweekly', 'monthly'];
export const PAY_TYPES = ['hourly', 'fixed'];

export const STATUS_LABELS = {
  todo: 'To do',
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  archived: 'Archived',
};

export const JOB_TYPE_LABELS = { maintenance: 'Maintenance', project: 'Project' };
// "Every week" rather than "Weekly", because the dropdown is read together
// with its field label: Repeats — Every week.
export const RECURRENCE_LABELS = { weekly: 'Every week', biweekly: 'Every two weeks', monthly: 'Every month' };
export const PAY_TYPE_LABELS = { hourly: 'Hourly', fixed: 'Fixed price' };

/**
 * A status as a person should read it.
 *
 * `jobs.status` is a plain TEXT column with NO check constraint, so a value
 * outside the five above is genuinely possible — legacy rows predate
 * isValidStatus, and nothing at the database level stops one. Printing the raw
 * token put "BLOCKED" on a card in an uppercase pill next to the real Blocked
 * marker (seen 2026-09-03); an unknown status is humanised instead.
 */
export function statusLabel(status) {
  if (!status) return STATUS_LABELS.todo;
  if (STATUS_LABELS[status]) return STATUS_LABELS[status];
  const words = String(status).replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Status → tone. THE map, for every screen that shows a job's stage.
 *
 * ── What this replaces, and what it cost ────────────────────────────────────
 *
 * There were five of these, and they disagreed. The worst was
 * OfficePage.js's jobTone(), which switched on `in_progress`, `scheduled` and
 * `pending` — none of which this app has ever had. The real statuses are the
 * five in JOB_STATUSES, frozen server-side at scheduling.js:12, and THREE of
 * them (todo, active, paused) matched no case and fell to the default. So on
 * the Office Jobs list and detail — the main jobs screen — To do, Active and
 * Paused all rendered as the same neutral grey pill, distinguishable only by
 * reading the word. That is the owner's "can't tell what is what", and it is
 * why this map is now one thing that everything imports.
 *
 * ── Why these six ───────────────────────────────────────────────────────────
 *
 * There are exactly six tones (PILL_TONES / JOBCARD_TONES — the same set) and
 * five statuses, so every stage gets its own and `stop` is left for the one
 * state that is not a stage: BLOCKED, which means do not go. Archived is
 * `neutral` and not `stop` for that reason — a job that has been put away is
 * not an emergency, and rendering it in the danger tone spends the loudest
 * colour in the app on the quietest state.
 *
 * This is the mapping Field cards already used. Office is the side that was
 * wrong.
 */
export const STATUS_TONES = {
  todo: 'sched',
  active: 'now',
  paused: 'warn',
  completed: 'ok',
  archived: 'neutral',
};

/**
 * Tokens `jobs.status` can hold that are not stages.
 *
 * The column is plain TEXT with no check constraint, so legacy rows really do
 * carry these (see statusLabel above, which humanises the same case).
 */
const LEGACY_TONES = { blocked: 'stop', cancelled: 'stop' };

/** The tone for a status. Unknown → neutral, never a colour that asserts something. */
export function statusTone(status) {
  return STATUS_TONES[status] || LEGACY_TONES[status] || 'neutral';
}

/**
 * Every badge a job card shows, in one place so the two screens that show
 * them cannot drift — and deduplicated by label, because a legacy job whose
 * STATUS is 'blocked' and which also carries a blocked_reason would otherwise
 * show "Blocked" twice.
 */
export function jobBadges(job) {
  if (!job) return [];
  const out = [{ key: 'status', label: statusLabel(job.status), tone: statusTone(job.status) }];
  if (job.no_client) out.push({ key: 'no_client', label: 'No client', tone: 'warn' });
  if (job.blocked_reason) out.push({ key: 'blocked', label: 'Blocked', tone: 'stop' });

  const seen = new Set();
  return out.filter((b) => {
    const k = b.label.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

// ── What a filter means, in one place ───────────────────────────────────────
//
// Office Jobs and the Schedule both offer "filter by worker" and the Schedule
// offers "filter by type". They are the SAME question asked on two screens, so
// they are answered here once. Two screens with two spellings of the same
// filter is how one of them quietly starts answering something else — the
// failure docs/PARITY_2026-09-08.md is a list of.

/** The <select> value for "nobody leads this job". Not a user id, so it is named. */
export const UNASSIGNED = 'unassigned';

/** The value for "no job type set" — jobs.job_type is nullable. */
export const NO_TYPE = 'none';

/**
 * What a type filter may offer. JOB_TYPES plus the untyped bucket, because
 * server/utils/scheduling.js normalizeJobType writes NULL for anything it does
 * not recognise, so most rows in a real database have no type at all and a
 * filter that cannot reach them can only ever hide work.
 */
export const TYPE_FILTER_VALUES = ['', ...JOB_TYPES, NO_TYPE];
export const TYPE_FILTER_LABELS = { '': 'All types', ...JOB_TYPE_LABELS, [NO_TYPE]: 'No type' };

/** Does this job carry the type a filter names? An empty filter matches all. */
export function matchesType(job, type) {
  if (!type) return true;
  return ((job && job.job_type) || NO_TYPE) === type;
}

/**
 * Does this job belong to the worker a filter names?
 *
 * Compared as STRINGS because a <select> value is text while jobs.user_id is a
 * number — `j.user_id === value` is false for every row, which looks exactly
 * like a filter that matches nothing.
 *
 * The LEAD pointer only (jobs.user_id), which is what the classic Schedule
 * board (`workerFilter`) and the classic Jobs list both filtered on, and what
 * Office Jobs' crew filter has always answered. The crew ROSTER
 * (job_assignees) is additive and a job can carry somebody who does not lead
 * it — the server's own "is this your job" test is lead OR roster
 * (server/routes/field.js:181) — so "every job I am on" is a real and
 * different question. It is not this one. Changing this predicate would change
 * what two shipped filters answer, silently, so it is written down rather than
 * decided in passing.
 */
export function matchesWorker(job, worker) {
  if (!worker) return true;
  if (worker === UNASSIGNED) return !job || job.user_id == null;
  return String(job && job.user_id) === String(worker);
}

/**
 * The crew, as a sentence, with the lead first and named as such.
 *
 * Moved here from office/records/Jobs.js on 2026-09-09 rather than copied:
 * the FIELD job card needs the same sentence (docs/PARITY_2026-09-08.md §8 —
 * a crew member could not see who else was on the job), and CLAUDE.md is
 * explicit that a second copy of a rule in a second screen is how the two end
 * up disagreeing about the same row. Office re-exports it, so its own call
 * sites and OfficeJobs.test.js are unchanged.
 *
 * Reads `assignees` (server/utils/crew.js, lead first) and falls back to the
 * lead pointer's own name — `user_name` in the Office payload, `lead_name` in
 * Field's. A job with a lead and no roster rows is a real shape: POST /jobs
 * and a claim both set jobs.user_id on its own.
 */
/**
 * The word for "nobody is on this job".
 *
 * A constant rather than a literal because two things compare against it —
 * crewLabel below, and the tests that pin what a dispatcher sees — and a blank
 * where a name goes reads as a rendering fault rather than as information.
 * Unassigned IS the state a dispatcher is hunting for.
 */
export const UNASSIGNED_LABEL = 'Unassigned';

export function crewSummary(job, { fallbackLead = '' } = {}) {
  if (!job) return UNASSIGNED_LABEL;
  const names = (job.assignees || []).map((a) => a.name).filter(Boolean);
  if (!names.length) return job.user_name || job.lead_name || fallbackLead || UNASSIGNED_LABEL;
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
}

/**
 * The same sentence, worded for a CARD FACT rather than a table cell.
 *
 * A table cell sits under a column heading that says "Crew", so the bare name
 * is unambiguous. A card fact does not: JobCard prints its facts as one
 * middot-joined run of muted text, and "Marcus Reyes · 3 of 5 done · 2 hrs
 * est." leaves the first item as the only one that does not say what it is —
 * next to a meta line that carries a client and a property, a bare name reads
 * as another place. Field's own card has said "Dave +2 crew" since
 * docs/PARITY_2026-09-08.md §8; this is that wording, in one place.
 *
 * `fallbackLead` exists because `assignees` comes from job_assignees ONLY
 * (server/utils/crew.js attachAssignees) while the Dispatch board GROUPS by
 * jobs.user_id. A job with a lead pointer and no roster row is a real shape —
 * POST /jobs and a claim both set user_id on its own — and without the
 * fallback such a job reads "Unassigned" while sitting under the heading of
 * the person it is assigned to. Two opposite statements about the same row.
 */
export function crewLabel(job, opts) {
  const who = crewSummary(job, opts);
  return who === UNASSIGNED_LABEL ? who : `${who} crew`;
}

/**
 * The tone for a JOB — the card's left border and the row's colour.
 *
 * `statusTone` answers for a status; this answers for a record, and the only
 * difference is that BLOCKED wins over every lifecycle stage. It is the one
 * state that means "do not go", which is why `stop` is not spent on any stage.
 *
 * Lives here rather than in apps/field/jobCardProps.js, where it was written,
 * because Dispatch needs the same answer: Board.js carried its own fifth copy
 * that switched on `status === 'completed'` and nothing else, so to do, active
 * and paused all rendered the same neutral grey on the crew board. That is the
 * owner's "statuses indistinguishable", in the same shape the Office greys had
 * (see STATUS_TONES above) and for the same reason — a second copy of a rule.
 *
 * Reads BOTH `blocked` (Field's boolean) and `blocked_reason` (the column):
 * the offline cache and the harness fixtures carry only the boolean, and a
 * card built from a cached bundle must not lose the one badge that means
 * do not drive there.
 */
export function jobCardTone(job) {
  if (!job) return 'neutral';
  if (job.blocked || job.blocked_reason) return 'stop';
  return statusTone(job.status);
}

/**
 * Every badge a JOB CARD shows — jobBadges plus the boolean-only blocked case.
 *
 * Moved here from apps/field/jobCardProps.js on 2026-09-10 rather than copied,
 * for the reason that file already gives: `shapeJob()` returns both `blocked`
 * and `blocked_reason`, jobBadges keys off the reason, and the Field offline
 * cache carries only the boolean. Dispatch's board shows the same five
 * statuses on the same primitive and must not grow a third vocabulary for
 * them. Deduped by label, so a legacy row whose STATUS is 'blocked' and which
 * also carries a reason does not say Blocked twice.
 */
export function jobCardBadges(job) {
  if (!job) return [];
  const badges = jobBadges(job);
  const blocked = job.blocked || job.blocked_reason;
  if (blocked && !badges.some((b) => String(b.label).toLowerCase() === 'blocked')) {
    badges.push({ key: 'blocked', label: 'Blocked', tone: 'stop' });
  }
  return badges;
}

/** Only a manager or an admin may assign work to somebody else. */
export function canAssignOthers(role) {
  return role === 'admin' || role === 'manager';
}

const str = (v) => (v === null || v === undefined ? '' : String(v));

/**
 * The form's starting values. `job` undefined means a new job.
 * Everything is a string because that is what an <input> holds; jobBody turns
 * them back into the types the API wants.
 */
/** ids with the lead on the front, once, as strings. */
function withLead(ids, lead) {
  const list = (ids || []).map(String).filter(Boolean);
  const l = lead === 0 || lead ? String(lead) : '';
  if (!l) return list;
  return list.includes(l) ? list : [l, ...list];
}

/**
 * Choosing a lead TICKS them, so nothing has to be added at send time (#549).
 *
 * Which side had to change was decided by the server, not by taste:
 * server/utils/crew.js setJobRoster resolves the lead as
 * `clean.includes(wantLead) ? wantLead : (clean.length ? clean[0] : null)`, so
 * a lead who is not on the roster it is sent is DISCARDED and replaced by
 * whoever sorts first. Sending the lead is therefore mandatory — jobBody has
 * always done it — and the form was the half telling the person something
 * else.
 *
 * A previous lead is kept on the crew when the lead changes. They were on the
 * job; being demoted from lead is not being sent home.
 */
export function applyLeadChange(form, userId) {
  const next = { ...form, user_id: str(userId) };
  next.assignee_ids = withLead(form.assignee_ids, next.user_id);
  return next;
}

/**
 * Tick or untick a crew member. The LEAD's row cannot be unticked: doing so
 * would send a roster the server would then override, which is the same
 * "showed one thing, saved another" the lead-ticking fixed. The UI disables
 * that row; this refuses it for anything reaching the function another way.
 */
export function toggleAssignee(form, id) {
  const key = String(id);
  const ids = (form.assignee_ids || []).map(String);
  if (ids.includes(key)) {
    if (key === str(form.user_id)) return form;
    return { ...form, assignee_ids: ids.filter((x) => x !== key) };
  }
  return { ...form, assignee_ids: [...ids, key] };
}

/**
 * What the property picker should OFFER: the chosen client's properties, plus
 * whatever this job is already carrying (#549).
 *
 * The picker was filled from propertiesFor() alone, so a job holding a
 * property that list does not contain — no client on the job, a property list
 * that 403'd, a row pointing at another client's property — rendered a select
 * whose value matched no option. A select with no matching option shows
 * nothing, so the person read "No property" while `property_id` was still in
 * the body being saved.
 *
 * Sending it is right: dropping a live job's address on an unrelated edit
 * would be worse. Showing it was the part that was missing. An off-list
 * property is named where a name is known and numbered where it is not —
 * better a number the office can look up than an empty box.
 */
export function propertyOptions(properties, form, { propertyName = '' } = {}) {
  const onList = propertiesFor(properties, form && form.client_id)
    .map((p) => ({ id: String(p.id), name: p.name || p.address || `Property #${p.id}`, onList: true }));
  const held = form && form.property_id ? String(form.property_id) : '';
  if (!held || onList.some((o) => o.id === held)) return onList;
  // The name comes from the JOB's own property_name (the server sends it) and
  // from nowhere else. Deliberately NOT looked up in `properties`: that list
  // is scoped to the chosen client, so a row found in it for an off-list id
  // belongs to somebody else, and borrowing its name would put another
  // client's address on this job's picker. A number the office can look up is
  // the honest answer.
  const name = propertyName || `Property #${held}`;
  return [{ id: held, name, onList: false }, ...onList];
}

/**
 * ── jobs.estimated_time is SECONDS. The field asks for MINUTES. (#620) ──────
 *
 * The column held two incompatible units until migration 0006: the classic
 * form wrote `(hours * 3600) + (minutes * 60)` and four server routes still
 * divide it by 3600 (`routes/dispatch.js:70`, `field.js:165`,
 * `timesheets.js:445` and `:676`), while the rebuilt form wrote the person's
 * minutes straight through. So a job saved here as "90 estimated minutes" came
 * back from GET /api/dispatch/board as `estimated_hours: 0`.
 *
 * Seconds won, because it is what those four routes and every pre-rebuild row
 * already meant. Minutes stay in the FIELD, because minutes are what a person
 * estimates a job in — nobody types 5400. These two functions are the only
 * place the two units meet, and jobFields.test.js pins the round trip.
 */

/** Stored seconds → what the minutes field shows. '' when there is no estimate. */
export function estimatedMinutes(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return '';
  // Rounded, not floored: the field's granularity is one minute, and a legacy
  // row holding 90 seconds should read as 2 rather than as 1. Re-saving such a
  // row does move it to the nearest minute — that is a bounded, visible change
  // to a number the person is looking at, unlike the 60x unit flip it replaces.
  const minutes = Math.round(n / 60);
  // Under half a minute there is no truthful minutes value to show. '' means
  // "no estimate", which is what the field already renders for 0 — showing a
  // literal '0' would invite a save that wrote 0 and lost the estimate without
  // the person seeing anything change.
  return minutes > 0 ? String(minutes) : '';
}

/** What the minutes field holds → the seconds the column stores. */
export function estimatedSeconds(minutes) {
  const n = Number(String(minutes ?? '').trim());
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n) * 60;
}

export function seedJob(job, { defaultDate = '' } = {}) {
  const assignees = Array.isArray(job?.assignees) ? job.assignees : [];
  const ids = assignees.map((a) => String(a.id ?? a.user_id ?? a)).filter(Boolean);
  return {
    title: str(job?.title),
    description: str(job?.description),
    client_id: str(job?.client_id),
    property_id: str(job?.property_id),
    user_id: job ? str(job.user_id) : '',
    // The lead is ALWAYS on the roster the form holds (#549). job_assignees
    // and jobs.user_id can disagree on legacy rows — the roster predates the
    // lead pointer being mirrored into it — and the form used to show the
    // roster while jobBody sent the union, so a person saw one crew and saved
    // another. Seeding the lead in means what is shown is what is sent.
    assignee_ids: withLead(ids, job?.user_id),
    status: job?.status && JOB_STATUSES.includes(job.status) ? job.status : 'todo',
    job_type: str(job?.job_type),
    recurrence: str(job?.recurrence),
    scheduled_date: str(job?.scheduled_date) || (job ? '' : defaultDate),
    scheduled_end_date: str(job?.scheduled_end_date),
    // SECONDS in the column, MINUTES in the field — see estimatedMinutes /
    // estimatedSeconds below. This used to seed the stored number verbatim,
    // which is half of #620.
    estimated_time: estimatedMinutes(job?.estimated_time),
    pay_amount: job?.pay_amount === null || job?.pay_amount === undefined ? '' : String(job.pay_amount),
    pay_type: job?.pay_type && PAY_TYPES.includes(job.pay_type) ? job.pay_type : 'hourly',
    status_note: '',
  };
}

/** A sentence naming what is wrong, or null when the form may be sent. */
export function validateJob(form) {
  if (!form.title || !form.title.trim()) return 'A job needs a title.';
  if (form.scheduled_date && form.scheduled_end_date && form.scheduled_end_date < form.scheduled_date) {
    return 'The end date is before the start date.';
  }
  if (form.estimated_time && !/^\d+$/.test(String(form.estimated_time).trim())) {
    return 'Estimated time must be a whole number of minutes.';
  }
  if (form.pay_amount !== '' && form.pay_amount !== null && Number.isNaN(Number(form.pay_amount))) {
    return 'Pay amount must be a number.';
  }
  return null;
}

const nullable = (v) => {
  const s = str(v).trim();
  return s === '' ? null : s;
};
const numOrNull = (v) => {
  const s = str(v).trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

/**
 * What the form POSTs or PUTs.
 *
 * `user_id` carries the LEAD worker, which payroll, the worker job list and
 * the daily digest all read (CLAUDE.md). The crew roster is additive and is
 * sent separately as assignee_ids; the server mirrors the lead back into
 * user_id, so the two cannot drift. 'unassigned' is the server's own sentinel
 * for "nobody", and is not the same as omitting the field.
 */
export function jobBody(form, { role = 'worker', editing = false } = {}) {
  const body = {
    title: form.title.trim(),
    description: nullable(form.description),
    client_id: numOrNull(form.client_id),
    property_id: numOrNull(form.property_id),
    status: JOB_STATUSES.includes(form.status) ? form.status : 'todo',
    job_type: JOB_TYPES.includes(form.job_type) ? form.job_type : null,
    recurrence: RECURRENCES.includes(form.recurrence) ? form.recurrence : null,
    scheduled_date: nullable(form.scheduled_date),
    scheduled_end_date: nullable(form.scheduled_end_date),
    // The field is minutes; the column is seconds (#620).
    estimated_time: estimatedSeconds(form.estimated_time),
    pay_amount: numOrNull(form.pay_amount),
    pay_type: PAY_TYPES.includes(form.pay_type) ? form.pay_type : 'hourly',
  };

  if (canAssignOthers(role)) {
    const ids = (form.assignee_ids || []).map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0);
    const lead = numOrNull(form.user_id);
    // BACKSTOP (#549). applyLeadChange and seedJob now put the lead on the
    // roster where the person can SEE them, so a form arriving here without
    // the lead means state was built some other way. Kept rather than
    // loosened, because the server discards a lead who is not on the roster it
    // is sent and hands the job to whoever sorts first — and jobs.user_id is
    // the pointer payroll reads.
    if (lead && !ids.includes(lead)) ids.unshift(lead);
    body.assignee_ids = ids;
    body.user_id = lead === null ? 'unassigned' : lead;
  }

  if (editing && form.status_note && form.status_note.trim()) {
    body.status_note = form.status_note.trim();
  }
  return body;
}

/** Properties belonging to the chosen client — the rest would be wrong here. */
export function propertiesFor(properties, clientId) {
  if (!Array.isArray(properties)) return [];
  if (!clientId) return [];
  return properties.filter((p) => String(p.client_id) === String(clientId));
}

/**
 * Changing the client invalidates a property belonging to the old one. Returns
 * the form with the property cleared when it no longer fits.
 */
export function applyClientChange(form, clientId, properties) {
  const next = { ...form, client_id: str(clientId) };
  const allowed = propertiesFor(properties, clientId).map((p) => String(p.id));
  if (next.property_id && !allowed.includes(next.property_id)) next.property_id = '';
  return next;
}
